import { useState } from 'react';
import { Color, Size, fill, formatDateOnly, t, wordedRefusal } from '@bms2/shared';
import { Button, DatePicker, Select } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { DetailList, DetailRow } from '../../components/composites';
import { useDateFormat, useOrganizationToday } from '../../shell/ViewerZone';
import { post, useApi } from '../records/api';
import { FormFields, FormFrame } from '../../components/forms/FormFrame';
import { useSaveNotes } from '../settings/SaveNote';
import styles from './ApprovalDelegations.module.css';
import queueStyles from './PendingApprovalsPage.module.css';
import settingsStyles from '../settings/settings.module.css';

/**
 * 🔒 The Delegations section under the Pending Approvals queue (piece 8;
 * decisions 10631, 11560; T938, T3610): the cover the viewer gave and the cover
 * they hold, + Add Cover from the viewer's own job, and End on a row they gave.
 * Cover is set by the approver alone, on the page every approver already opens;
 * the server's cycle guard refuses a loop in words.
 */
export const APPROVAL_DELEGATIONS_PATH = '/approvals/delegations';

/** The End route of one cover row. */
export function approvalDelegationEndPath(id: string): string {
  return `${APPROVAL_DELEGATIONS_PATH}/${encodeURIComponent(id)}/end`;
}

/** One row as the server sends it (`ApprovalDelegationView`). */
export interface ApprovalDelegationRowView {
  id: string;
  direction: 'gave' | 'holds';
  fromName: string;
  toName: string;
  departmentName: string | null;
  startDate: string;
  endDate: string | null;
  mayEnd: boolean;
}

/** What the section reads (`ApprovalDelegationsRead`). */
export interface ApprovalDelegationsAnswer {
  rows: ApprovalDelegationRowView[];
  people: { employmentId: string; name: string }[];
  departments: { id: string; name: string }[];
  mayAdd: boolean;
}

/** The value the department picker sends for every department, since a department id is never this word. */
const ALL = 'all';

export function ApprovalDelegationsSection({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const read = useApi<ApprovalDelegationsAnswer>(APPROVAL_DELEGATIONS_PATH, apiBaseUrl);
  const notes = useSaveNotes();
  const dateFormat = useDateFormat();
  const [adding, setAdding] = useState(false);
  const span = (row: ApprovalDelegationRowView) => (row.endDate
    ? fill(t('settings.approvals.delegations.span'), { start: formatDateOnly(row.startDate, dateFormat), end: formatDateOnly(row.endDate, dateFormat) })
    : fill(t('settings.approvals.delegations.from'), { start: formatDateOnly(row.startDate, dateFormat) }));
  const end = async (row: ApprovalDelegationRowView) => {
    const written = await post<{ ended: 'ended' | 'removed' }>(approvalDelegationEndPath(row.id), {}, apiBaseUrl);
    if (written.ok) {
      notes.say('delegations', written.data?.ended === 'removed' ? t('settings.approvals.delegations.removed') : t('settings.approvals.delegations.ended'), 'good');
      read.reload();
    } else {
      notes.say('delegations', wordedRefusal(t('settings.approvals.delegations.notSaved'), written.message), 'error');
    }
  };
  const ready = read.state.kind === 'ready' ? read.state.data : null;
  return (
    <section className={styles.section} data-approvals-delegations-section="true">
      <div className={styles.head}>
        <h2 className={queueStyles.heading}>{t('settings.approvals.delegations.title')}</h2>
        {ready?.mayAdd ? <Button size={Size.Small} color={Color.Navy} text={[t('settings.approvals.delegations.newCover')]} actions={[() => setAdding(true)]} /> : null}
      </div>
      <p className={settingsStyles.hint}>{t('settings.approvals.delegations.hint')}</p>
      <AsyncStatePanel state={read.state} emptyAction={t('settings.approvals.delegations.none')} onRetry={read.reload}>
        {(answer) => (
          <>
            {answer.mayAdd ? null : <p className={settingsStyles.hint}>{t('settings.approvals.delegations.noJob')}</p>}
            <DetailList look="separator" data-approval-delegations={answer.rows.length}>
              {answer.rows.map((row) => (
                <DetailRow
                  key={row.id}
                  data-approval-delegation={row.direction}
                  lead={<span className={settingsStyles.emphasis}>{fill(t(row.direction === 'gave' ? 'settings.approvals.delegations.gave' : 'settings.approvals.delegations.holds'), { name: row.direction === 'gave' ? row.toName : row.fromName })}</span>}
                  detail={[
                    <span key="span">{span(row)}</span>,
                    <span key="department">{row.departmentName ?? t('settings.approvals.delegations.allDepartments')}</span>,
                  ]}
                  actions={row.mayEnd ? <Button size={Size.Small} color={Color.Inactive} text={[t('settings.approvals.delegations.end')]} actions={[() => void end(row)]} /> : undefined}
                />
              ))}
              {answer.rows.length === 0 ? <DetailRow detail={t('settings.approvals.delegations.none')} /> : null}
            </DetailList>
            {notes.at('delegations')}
            {/* 🔨 Mounted only while open, so each opening starts empty (decision 2803). */}
            {adding ? (
              <AddCoverWindow
                answer={answer}
                apiBaseUrl={apiBaseUrl}
                onClose={() => setAdding(false)}
                onSaved={() => {
                  setAdding(false);
                  notes.say('delegations', t('settings.saved'), 'good');
                  read.reload();
                }}
              />
            ) : null}
          </>
        )}
      </AsyncStatePanel>
    </section>
  );
}

/** + Add Cover, drawn through the Form frame (decision 3060): the person, the first and last day, and one department or all of them. */
function AddCoverWindow({ answer, apiBaseUrl, onClose, onSaved }: { answer: ApprovalDelegationsAnswer; apiBaseUrl: string; onClose: () => void; onSaved: () => void }) {
  // 🔒 The organization's day, the one the server refuses an earlier first day by, so the default is never refused (review of batch OP1).
  const today = useOrganizationToday();
  const [person, setPerson] = useState('');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState('');
  const [department, setDepartment] = useState(ALL);
  const [refusal, setRefusal] = useState<string | null>(null);
  const valid = person !== '' && startDate !== '';
  const typed = person !== '' || startDate !== today || endDate !== '' || department !== ALL;
  const save = async () => {
    const written = await post(APPROVAL_DELEGATIONS_PATH, { toEmploymentId: person, startDate, endDate: endDate === '' ? null : endDate, departmentId: department === ALL ? null : department }, apiBaseUrl);
    if (written.ok) onSaved();
    else setRefusal(wordedRefusal(t('settings.approvals.delegations.notSaved'), written.message));
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('settings.approvals.delegations.addCover')}
      dataForm="settings-add-approval-cover"
      typed={typed}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('settings.approvals.delegations.addCover'), onAct: save, disabled: !valid, disabledReason: t('settings.approvals.delegations.pickPerson') }}
    >
      <FormFields dataFields="add-approval-cover">
        <div className={settingsStyles.addWindowBody} data-settings-card="add-approval-cover">
          <Select size={Size.Medium} label={t('settings.approvals.delegations.person')} value={person} onChange={setPerson} required placeholder={t('settings.none')} options={answer.people.map((one) => ({ value: one.employmentId, label: one.name }))} dataAttribute={{ 'data-cover-person': 'true' }} />
          <DatePicker size={Size.Medium} label={t('settings.approvals.delegations.firstDay')} value={startDate} onChange={setStartDate} min={today} required name="cover-first-day" />
          <DatePicker size={Size.Medium} label={t('settings.approvals.delegations.lastDay')} value={endDate} onChange={setEndDate} min={startDate || today} name="cover-last-day" />
          <p className={settingsStyles.hint}>{t('settings.approvals.delegations.lastDayHint')}</p>
          <Select size={Size.Medium} label={t('settings.approvals.delegations.department')} value={department} onChange={setDepartment} options={[{ value: ALL, label: t('settings.approvals.delegations.allDepartments') }, ...answer.departments.map((one) => ({ value: one.id, label: one.name }))]} dataAttribute={{ 'data-cover-department': 'true' }} />
        </div>
      </FormFields>
    </FormFrame>
  );
}
